import React, { createContext, useCallback, useReducer, useContext, useRef } from "react";

import { reducer } from "./reducer";
import { clearSession } from "../utils/session";

const AppContext = createContext();

const getUserFromLocalStorage = () => {
  const stored = localStorage.getItem("user");

  if (!stored) {
    return null;
  }

  try {
    const user = JSON.parse(stored);

    // Sessions stored before Auth0 carry a bare role word as `username` and no `role`.
    // They have no access token either, so treat them as signed out rather than let
    // them through with every permission predicate silently false.
    if (!user?.role) {
      clearSession();
      return null;
    }

    return user;
  } catch (err) {
    clearSession();
    return null;
  }
};

const getActiveKeyFromLocalStorage = () => {
  const activeKey = localStorage.getItem("activeKey");
  return activeKey || "/tenants";
};

// Built on mount rather than at module load, so it reflects localStorage as it is when
// the provider actually renders.
const buildInitialState = () => ({
  user: getUserFromLocalStorage(),
  activeKey: getActiveKeyFromLocalStorage()
});

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, buildInitialState);
  const navigationGuardRef = useRef(null);

  const loginUser = ({ email, username, role }) => {
    // `email` is the identity the backend authenticates, `role` drives the permission
    // predicates, and `username` is the short form the header displays.
    const userData = { email, username, role };
    dispatch({
      type: "LOGIN_USER",
      payload: userData
    });
    dispatch({
      type: "SET_ACTIVE_KEY",
      payload: "/tenants" // default page on login
    });
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("activeKey", "/tenants");
  };

  const logoutUser = () => {
    dispatch({
      type: "LOGOUT_USER"
    });
    // Also drops the access token and the in-memory Auth0 credentials.
    clearSession();
  };

  const setActiveKey = (key) => {
    dispatch({
      type: "SET_ACTIVE_KEY",
      payload: key
    });
    localStorage.setItem("activeKey", key);
  };

  const registerNavigationGuard = useCallback((guard) => {
    navigationGuardRef.current = guard;
    return () => {
      if (navigationGuardRef.current === guard) {
        navigationGuardRef.current = null;
      }
    };
  }, []);

  const confirmNavigation = useCallback(async (context) => {
    if (!navigationGuardRef.current) {
      return true;
    }
    return navigationGuardRef.current(context);
  }, []);

  return (
    <AppContext.Provider value={{ ...state, loginUser, logoutUser, setActiveKey, registerNavigationGuard, confirmNavigation }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
